import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import {
  api,
  ApiError,
  type AgentCredentialView,
  type AgentLabel,
  type NewAgentCredential,
} from '../lib/api'
import { Banner, Button, Card, Chip, TextField } from '../ui'
import { AgentSendControls } from './AgentSendControls'

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null

export function AgentCredentials() {
  const queryClient = useQueryClient()
  const agents = useQuery({ queryKey: ['agents'], queryFn: api.agents })
  const activity = useQuery({ queryKey: ['agent-activity'], queryFn: api.agentActivity })

  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<string[]>(['conversations:read', 'tickets:read'])
  // Defaults to scoped rather than whole-workspace: the safe option should be the one you
  // get by not thinking about it.
  const [unrestricted, setUnrestricted] = useState(false)
  const [labelIds, setLabelIds] = useState<string[]>([])
  const [issued, setIssued] = useState<NewAgentCredential | null>(null)
  const [copied, setCopied] = useState(false)

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['agents'] })

  const create = useMutation({
    mutationFn: () => api.createAgent({ name, scopes, unrestricted, labelIds }),
    onSuccess: async (credential) => {
      setIssued(credential)
      setName('')
      setLabelIds([])
      setCopied(false)
      await refresh()
    },
  })

  const revoke = useMutation({ mutationFn: api.revokeAgent, onSuccess: refresh })

  if (agents.isPending) {
    return <div className="skeleton" style={{ height: '220px' }} />
  }

  // Previously `isLoading || !agents.data`, which left the skeleton on screen forever once the
  // query had failed and stopped retrying: not loading, no data, no error branch. The page
  // looked like it was still thinking, indefinitely.
  if (!agents.data) {
    const message =
      agents.error instanceof ApiError || agents.error instanceof Error
        ? agents.error.message
        : 'Agent credentials could not be loaded.'
    return (
      <div className="stack gap-2">
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>{message}</p>
        <div>
          <button type="button" className="cad-btn" data-variant="quiet" onClick={() => void agents.refetch()}>
            Try again
          </button>
        </div>
      </div>
    )
  }

  const { credentials, scopes: catalog, labels, enabled, endpoint } = agents.data
  const active = credentials.filter((c) => !c.revokedAt)
  const revoked = credentials.filter((c) => c.revokedAt)

  const toggleLabel = (id: string) =>
    setLabelIds((current) =>
      current.includes(id) ? current.filter((l) => l !== id) : [...current, id],
    )

  const toggleScope = (id: string) =>
    setScopes((current) =>
      current.includes(id) ? current.filter((s) => s !== id) : [...current, id],
    )

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (name.trim() && scopes.length > 0 && (unrestricted || labelIds.length > 0)) create.mutate()
  }

  const error = create.error ?? revoke.error
  const errorMessage = error instanceof ApiError || error instanceof Error ? error.message : undefined

  return (
    <div className="stack gap-6" style={{ maxWidth: '900px' }}>
      <div className="stack gap-2">
        <span className="eyebrow">Agent access</span>
        <h1 className="headline-large">AI agents</h1>
        <p className="body-large text-muted" style={{ maxWidth: '58ch' }}>
          One credential, two ways in: an MCP server for AI assistants, and a REST API
          (<code>/api/v1/…</code>) for everything else. Unlike a bare API key it names who is
          behind the software, and every call either surface makes is recorded here.
        </p>
      </div>

      {!enabled && (
        <Banner tone="warn">
          The MCP Server module is not on your plan. You can still see this page, but credentials
          cannot be issued and the endpoint will refuse connections.
        </Banner>
      )}

      {errorMessage && <Banner tone="error">{errorMessage}</Banner>}

      {enabled && <AgentSendControls />}

      {issued && (
        <Card variant="outlined">
          <div className="stack gap-3">
            <div className="row between wrap gap-3">
              <h2 className="title-large">Copy your token now</h2>
              <Chip tone="warn">Shown once</Chip>
            </div>
            <p className="body-medium text-muted">
              We store only a hash of this token, so it cannot be shown again. If you lose it,
              revoke the credential and issue a new one.
            </p>
            <div className="token-row">
              <code className="token">{issued.token}</code>
              <Button
                variant="tonal"
                onClick={() => {
                  navigator.clipboard?.writeText(issued.token)
                  setCopied(true)
                }}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <details>
              <summary className="body-medium">How to connect an MCP client</summary>
              <pre className="snippet">{`{
  "mcpServers": {
    "wami": {
      "url": "${endpoint}",
      "headers": { "Authorization": "Bearer ${issued.token}" }
    }
  }
}`}</pre>
            </details>
            <details>
              <summary className="body-medium">How to call the REST API</summary>
              <pre className="snippet">{`curl ${new URL('/api/v1/tickets', endpoint).toString()} \\
  -H "Authorization: Bearer ${issued.token}"`}</pre>
            </details>
            <div className="row">
              <Button variant="text" onClick={() => setIssued(null)}>
                I have saved it
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card variant="outlined">
        <form className="stack gap-4" onSubmit={submit} noValidate>
          <h2 className="title-large">Issue a credential</h2>
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Claude Desktop — ops laptop"
            help="Only for you. Name it after the client and machine so you can revoke the right one."
            required
          />
          <div className="stack gap-2">
            <span className="label-large">Permissions</span>
            {catalog.map((scope) => (
              <label key={scope.id} className="scope-row">
                <input
                  type="checkbox"
                  checked={scopes.includes(scope.id)}
                  onChange={() => toggleScope(scope.id)}
                />
                <span className="stack gap-1">
                  <span className="row gap-2 wrap items-baseline">
                    <span className="body-medium" style={{ fontWeight: 560 }}>
                      {scope.label}
                    </span>
                    {scope.write && <Chip tone="warn">Can change data</Chip>}
                  </span>
                  <span className="body-small text-muted">{scope.description}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="stack gap-2">
            <span className="label-large">Which conversations</span>
            <p className="body-small text-muted">
              Permissions decide what an agent can do. This decides what it can reach.
            </p>
            <label className="scope-row">
              <input
                type="radio"
                name="reach"
                checked={!unrestricted}
                onChange={() => setUnrestricted(false)}
              />
              <span className="stack gap-1">
                <span className="body-medium" style={{ fontWeight: 560 }}>
                  Only these labels
                </span>
                <span className="body-small text-muted">
                  The agent sees chats and tickets carrying a label you pick, and nothing else.
                </span>
              </span>
            </label>
            {!unrestricted && (
              <div className="label-picker">
                {labels.length === 0 ? (
                  <p className="body-small text-muted">
                    {/* Was "Create them in the inbox" — the inbox has no label-creation control;
                        api.createLabel is reachable from exactly one screen, Settings → Labels.
                        The instruction sent people somewhere the action does not exist. */}
                    This workspace has no labels yet.{' '}
                    <a href="#settings">Create them in Settings → Labels</a>, or grant the whole
                    workspace below.
                  </p>
                ) : (
                  labels.map((label) => (
                    <button
                      key={label.id}
                      type="button"
                      className="label-swatch"
                      aria-pressed={labelIds.includes(label.id)}
                      onClick={() => toggleLabel(label.id)}
                    >
                      <span className="label-dot" style={{ background: label.color }} />
                      {label.name}
                    </button>
                  ))
                )}
              </div>
            )}
            <label className="scope-row">
              <input
                type="radio"
                name="reach"
                checked={unrestricted}
                onChange={() => setUnrestricted(true)}
              />
              <span className="stack gap-1">
                <span className="row gap-2 wrap items-baseline">
                  <span className="body-medium" style={{ fontWeight: 560 }}>
                    The whole workspace
                  </span>
                  <Chip tone="warn">Every conversation</Chip>
                </span>
                <span className="body-small text-muted">
                  Including private team notes on every chat, and any label added later.
                </span>
              </span>
            </label>
          </div>

          <div className="row">
            <Button
              type="submit"
              disabled={
                !enabled ||
                create.isPending ||
                !name.trim() ||
                scopes.length === 0 ||
                (!unrestricted && labelIds.length === 0)
              }
            >
              {create.isPending ? 'Issuing…' : 'Issue credential'}
            </Button>
          </div>
        </form>
      </Card>

      {active.length > 0 && (
        <div className="stack gap-4">
          <h2 className="title-large">Active credentials</h2>
          {active.map((credential) => (
            <CredentialCard
              key={credential.id}
              credential={credential}
              labels={labels}
              onRevoke={() => revoke.mutate(credential.id)}
              revoking={revoke.isPending}
            />
          ))}
        </div>
      )}

      {activity.data && activity.data.actions.length > 0 && (
        <Card variant="outlined">
          <div className="stack gap-3">
            <h2 className="title-large">Recent agent activity</h2>
            <div className="cad-grid-frame">
              <table className="cad-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Credential</th>
                    <th>Tool</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.data.actions.map((action) => (
                    <tr key={action.id}>
                      <td className="text-muted">{when(action.createdAt)}</td>
                      <td>{action.credentialName}</td>
                      <td className="mono">{action.tool}</td>
                      <td>
                        <Chip
                          tone={
                            action.outcome === 'ok'
                              ? 'success'
                              : action.outcome === 'denied'
                                ? 'warn'
                                : 'error'
                          }
                        >
                          {action.outcome === 'ok'
                            ? `${action.affected ?? 0} ${(action.affected ?? 0) === 1 ? 'record' : 'records'}`
                            : action.outcome === 'denied'
                              ? 'Refused'
                              : 'Failed'}
                        </Chip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      )}

      {revoked.length > 0 && (
        <details className="stack gap-2">
          <summary className="body-medium text-muted">
            {revoked.length} revoked credential{revoked.length === 1 ? '' : 's'}
          </summary>
          <div className="stack gap-2" style={{ paddingTop: '12px' }}>
            {revoked.map((c) => (
              <div key={c.id} className="row between wrap gap-3">
                <span className="body-medium text-muted">
                  {c.name} · <span className="mono">{c.displayHint}</span>
                </span>
                <span className="body-small text-muted">Revoked {when(c.revokedAt)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  )
}

function CredentialCard({
  credential,
  labels,
  onRevoke,
  revoking,
}: {
  credential: AgentCredentialView
  labels: AgentLabel[]
  onRevoke: () => void
  revoking: boolean
}) {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState(false)
  const [editingReach, setEditingReach] = useState(false)
  const [unrestricted, setUnrestricted] = useState(credential.unrestricted)
  const [labelIds, setLabelIds] = useState<string[]>(credential.labelIds)

  const updateAccess = useMutation({
    mutationFn: () => api.updateAgentAccess(credential.id, { unrestricted, labelIds }),
    onSuccess: () => {
      setEditingReach(false)
      void queryClient.invalidateQueries({ queryKey: ['agents'] })
    },
  })

  const toggleLabel = (id: string) =>
    setLabelIds((current) => (current.includes(id) ? current.filter((l) => l !== id) : [...current, id]))

  return (
    <Card variant="outlined">
      <div className="stack gap-4">
        <div className="row between wrap gap-3">
          <div className="stack gap-1">
            <span className="title-medium">{credential.name}</span>
            <span className="body-small text-muted mono">
              {credential.id} · {credential.displayHint}
            </span>
          </div>
          <div className="row gap-2 wrap">
            {credential.scopes.map((scope) => (
              <Chip key={scope} tone="assist">
                {scope}
              </Chip>
            ))}
          </div>
        </div>

        {/* Reach is stated separately from permissions, because a read-only credential
            over every conversation is the broad one people forget they granted. */}
        {editingReach ? (
          <div className="stack gap-2">
            <span className="label-large">Which conversations</span>
            <label className="scope-row">
              <input type="radio" name={`reach-${credential.id}`} checked={!unrestricted} onChange={() => setUnrestricted(false)} />
              <span className="body-medium" style={{ fontWeight: 560 }}>Only these labels</span>
            </label>
            {!unrestricted && (
              <div className="label-picker">
                {labels.length === 0 ? (
                  <p className="body-small text-muted">This workspace has no labels yet.</p>
                ) : (
                  labels.map((label) => (
                    <button
                      key={label.id}
                      type="button"
                      className="label-swatch"
                      aria-pressed={labelIds.includes(label.id)}
                      onClick={() => toggleLabel(label.id)}
                    >
                      <span className="label-dot" style={{ background: label.color }} />
                      {label.name}
                    </button>
                  ))
                )}
              </div>
            )}
            <label className="scope-row">
              <input type="radio" name={`reach-${credential.id}`} checked={unrestricted} onChange={() => setUnrestricted(true)} />
              <span className="row gap-2 wrap items-baseline">
                <span className="body-medium" style={{ fontWeight: 560 }}>The whole workspace</span>
                <Chip tone="warn">Every conversation</Chip>
              </span>
            </label>
            {updateAccess.error && (
              <Banner tone="error">
                {updateAccess.error instanceof ApiError || updateAccess.error instanceof Error
                  ? updateAccess.error.message
                  : 'Could not save that.'}
              </Banner>
            )}
            <div className="row gap-2 wrap">
              <Button
                variant="tonal"
                disabled={(!unrestricted && labelIds.length === 0) || updateAccess.isPending}
                onClick={() => updateAccess.mutate()}
              >
                {updateAccess.isPending ? 'Saving…' : 'Save changes'}
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  setEditingReach(false)
                  setUnrestricted(credential.unrestricted)
                  setLabelIds(credential.labelIds)
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="row gap-2 wrap items-baseline">
            <span className="body-small text-muted">Can reach</span>
            {credential.unrestricted ? (
              <Chip tone="warn">Every conversation</Chip>
            ) : credential.labelIds.length === 0 ? (
              <Chip tone="assist">Nothing — no labels granted</Chip>
            ) : (
              credential.labelIds.map((id) => {
                const label = labels.find((l) => l.id === id)
                return (
                  <span key={id} className="label-swatch">
                    <span
                      className="label-dot"
                      style={{ background: label?.color ?? 'var(--md-outline)' }}
                    />
                    {label?.name ?? id}
                  </span>
                )
              })
            )}
            <Button variant="text" onClick={() => setEditingReach(true)}>
              Edit
            </Button>
          </div>
        )}

        {/*
          The chain is the reason this is a credential and not an API key. Each row states
          what is actually known and how, which is why the operator row is presented as a
          claim rather than dressed up with a verified tick.
        */}
        <div className="chain-grid">
          {credential.chain.map((link) => (
            <div key={link.key} className="chain-link">
              <span className="chain-q">{link.question}</span>
              <span className="chain-label">{link.label}</span>
              <span className="chain-value">
                {link.key === 'operator'
                  ? (credential.operatorClaim ?? 'Not connected yet')
                  : link.key === 'representative'
                    ? credential.issuedByName
                    : link.key === 'agent'
                      ? credential.id
                      : 'This workspace'}
              </span>
              <Chip tone={link.status === 'verified' ? 'success' : 'warn'}>
                {link.status === 'verified' ? 'Verified' : 'Claimed'}
              </Chip>
              <span className="chain-basis">{link.basis}</span>
            </div>
          ))}
        </div>

        <div className="row between wrap gap-3">
          <span className="body-small text-muted">
            {credential.lastUsedAt
              ? `Last used ${when(credential.lastUsedAt)}`
              : 'Never used'}
          </span>
          {confirming ? (
            <div className="row gap-2 wrap">
              <Button variant="filled" disabled={revoking} onClick={onRevoke}>
                {revoking ? 'Revoking…' : 'Yes, revoke'}
              </Button>
              <Button variant="text" onClick={() => setConfirming(false)}>
                Keep
              </Button>
            </div>
          ) : (
            <Button variant="text" onClick={() => setConfirming(true)}>
              Revoke
            </Button>
          )}
        </div>

        {confirming && (
          <Banner tone="warn">
            Any client using this token stops working immediately. This cannot be undone.
          </Banner>
        )}
      </div>
    </Card>
  )
}
